import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Phone, 
  Edit, 
  PartyPopper, 
  XCircle,
  Eye,
  Calendar,
  Clock,
  Loader2,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { normalizeWhatsAppPhone } from '@/lib/messageTemplates';
import { plural } from '@/lib/plural';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Quote, QuoteItem, Notification } from '@/types/database';

const documentTypeInfo: Record<string, { label: string; feminine: boolean }> = {
  'orcamento': { label: 'Orçamento', feminine: false },
  'pedido': { label: 'Pedido', feminine: false },
  'recibo': { label: 'Recibo', feminine: false },
  'ordem_servico': { label: 'Ordem de serviço', feminine: true },
  'cotacao': { label: 'Cotação', feminine: true },
  'proposta': { label: 'Proposta', feminine: true },
  'proposta_comercial': { label: 'Proposta comercial', feminine: true },
  'fatura': { label: 'Fatura', feminine: true },
  'nota_fiscal': { label: 'Nota fiscal', feminine: true },
  'contrato': { label: 'Contrato', feminine: false },
  'estimativa': { label: 'Estimativa', feminine: true },
  'plano_trabalho': { label: 'Plano de trabalho', feminine: false },
  'comprovante': { label: 'Comprovante', feminine: false },
};

const getDocumentTypeLabel = (type: string) => {
  return documentTypeInfo[type]?.label || 'Orçamento';
};

const getDocumentTypeInfo = (type: string) => {
  return documentTypeInfo[type] || { label: 'Orçamento', feminine: false };
};

interface QuoteWithItems extends Quote {
  items?: QuoteItem[];
  rejection_reason?: string | null;
}

export default function NotificationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [quote, setQuote] = useState<QuoteWithItems | null>(null);

  useEffect(() => {
    if (!user || !id) {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch notification
        const { data: notifData, error: notifError } = await supabase
          .from('notifications')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();

        if (notifError || !notifData) {
          console.error('Error fetching notification:', notifError);
          navigate('/dashboard');
          return;
        }

        setNotification(notifData as Notification);

        // Mark as read if unread
        if (!notifData.read) {
          await supabase
            .from('notifications')
            .update({ read: true })
            .eq('id', id);
        }

        // Fetch quote if quote_id exists
        if (notifData.quote_id) {
          const { data: quoteData, error: quoteError } = await supabase
            .from('quotes')
            .select('*')
            .eq('id', notifData.quote_id)
            .single();

          if (quoteError) {
            console.error('Error fetching quote:', quoteError);
          } else if (quoteData) {
            // Fetch quote items
            const { data: itemsData } = await supabase
              .from('quote_items')
              .select('*')
              .eq('quote_id', quoteData.id)
              .order('position');

            setQuote({
              ...quoteData,
              items: itemsData || [],
            } as QuoteWithItems);
          }
        }
      } catch (err) {
        console.error('Error:', err);
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user, navigate]);

  const handleCall = () => {
    if (quote?.client_phone) {
      const cleanPhone = quote.client_phone.replace(/\D/g, '');
      window.open(`tel:${cleanPhone}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    if (quote?.client_phone) {
      // `startsWith('55')` NÃO detecta DDI: 55 também é DDD (Santa Maria/RS), e
      // um celular de lá saía truncado (#35b). O helper único decide por
      // COMPRIMENTO e trata um `+` inicial como "já tem país".
      const phoneWithCountry = normalizeWhatsAppPhone(quote.client_phone);
      window.open(`https://wa.me/${phoneWithCountry}`, '_blank');
    }
  };

  const handleEdit = () => {
    if (quote) {
      navigate(`/quotes/${quote.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!notification) {
    return null;
  }

  const isApproval = notification.type === 'approval';
  const isRejection = notification.type === 'rejection';
  const isView = notification.type === 'view';
  
  const statusColor = isApproval 
    ? 'bg-emerald-500' 
    : isRejection 
      ? 'bg-red-500' 
      : 'bg-blue-500';
  const statusIcon = isApproval ? (
    <PartyPopper className="h-8 w-8 text-emerald-600" />
  ) : isRejection ? (
    <XCircle className="h-8 w-8 text-red-600" />
  ) : (
    <Eye className="h-8 w-8 text-blue-600" />
  );
  const statusBgLight = isApproval 
    ? 'bg-emerald-100' 
    : isRejection 
      ? 'bg-red-100' 
      : 'bg-blue-100';
  
  // Dynamic status text based on document type and gender
  const docInfo = quote ? getDocumentTypeInfo(quote.document_type) : { label: 'Orçamento', feminine: false };
  const statusText = isApproval
    ? `${docInfo.label} ${docInfo.feminine ? 'aprovada' : 'aprovado'}`
    : isRejection
      ? `${docInfo.label} ${docInfo.feminine ? 'rejeitada' : 'rejeitado'}`
      : `${docInfo.label} ${docInfo.feminine ? 'visualizada' : 'visualizado'}`;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Detalhes da notificação</h1>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-24">
        {/* Status Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <div className={`${statusBgLight} p-4 rounded-full mb-4`}>
                {statusIcon}
              </div>
              <Badge className={`${statusColor} text-white text-lg px-4 py-1 mb-2`}>
                {statusText}
              </Badge>
              {quote && (
                <p className="text-muted-foreground mt-2">
                  {docInfo.feminine ? 'Sua' : 'Seu'} {docInfo.label.toLowerCase()} "{quote.title || quote.client_name}" foi {
                    isApproval 
                      ? (docInfo.feminine ? 'aprovada' : 'aprovado')
                      : isRejection 
                        ? (docInfo.feminine ? 'rejeitada' : 'rejeitado')
                        : (docInfo.feminine ? 'visualizada' : 'visualizado')
                  }!
                </p>
              )}
              <p className="text-sm text-muted-foreground/70 mt-2">
                {formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                  locale: ptBR,
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Rejection Reason Card */}
        {isRejection && notification.rejection_reason && (
          <Card className="border-red-200 bg-red-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-red-700 flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Motivo da rejeição
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-800">{notification.rejection_reason}</p>
            </CardContent>
          </Card>
        )}

        {/* Quote Details Card */}
        {quote && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Dados do {getDocumentTypeLabel(quote.document_type)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quote.title && (
                <div>
                  <p className="text-sm text-muted-foreground">Título</p>
                  <p className="font-medium">{quote.title}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-muted-foreground">Cliente</p>
                <p className="font-medium">{quote.client_name || 'Não informado'}</p>
              </div>

              {quote.client_phone && (
                <div>
                  <p className="text-sm text-muted-foreground">Telefone</p>
                  <p className="font-medium">{quote.client_phone}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground">Valor total</p>
                <p className="font-bold text-lg text-primary">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(quote.total)}
                </p>
              </div>

              <div className="flex gap-4">
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Criado em
                  </p>
                  <p className="font-medium">
                    {format(new Date(quote.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                  </p>
                </div>
                {quote.approved_at && (
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {isApproval ? 'Aprovado em' : 'Rejeitado em'}
                    </p>
                    <p className="font-medium">
                      {format(new Date(quote.approved_at), 'dd/MM/yyyy', { locale: ptBR })}
                    </p>
                  </div>
                )}
              </div>

              {/* Items Summary */}
              {quote.items && quote.items.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Itens ({quote.items.length})
                  </p>
                  <div className="space-y-2">
                    {quote.items.slice(0, 3).map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm bg-muted/50 p-2 rounded"
                      >
                        <span className="truncate flex-1">{item.name}</span>
                        <span className="font-medium ml-2">
                          {new Intl.NumberFormat('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          }).format(item.total)}
                        </span>
                      </div>
                    ))}
                    {quote.items.length > 3 && (
                      <p className="text-xs text-muted-foreground text-center">
                        +{plural(quote.items.length - 3, 'item', 'itens')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Client Contact Actions */}
        {quote?.client_phone && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Contatar cliente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Button3D
                  color="gray"
                  className="flex-1"
                  onClick={handleCall}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Ligar
                </Button3D>
                <Button3D
                  color="whatsapp"
                  className="flex-1"
                  onClick={handleWhatsApp}
                >
                  <WhatsAppIcon className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button3D>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed Edit Button */}
      {quote && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
          <Button3D
            className="w-full"
            onClick={handleEdit}
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar {getDocumentTypeLabel(quote.document_type)}
          </Button3D>
        </div>
      )}
    </div>
  );
}
